import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPencil, faBuilding, faLayerGroup, faUpload } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../../components/modal/modal';
import axiosInstance from '../../../AxiosInstance';

const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: '6px',
    border: '1px solid #CBD5E1', fontSize: '13px',
    outline: 'none', marginBottom: '4px', boxSizing: 'border-box',
};
const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: '#475569',
    display: 'block', marginBottom: '3px', marginTop: '10px',
};

const EMPTY_BUILDING = { name: '', address: '' };
const EMPTY_FLOOR = { floor_number: '', name: '', width_meters: '', height_meters: '', path_loss_exponent: 2.5, floorPlanFile: null, existingFloorPlanUrl: null };

/**
 * IndoorBuildingManagement
 * Props:
 *   buildings  - Building[] (with floors)
 *   projectId  - number
 *   onRefresh  - () => void
 */
export default function IndoorBuildingManagement({ buildings = [], projectId, onRefresh }) {
    const { t } = useTranslation();
    const [expandedBuilding, setExpandedBuilding] = useState(null);

    // Building modal
    const [showBuildingModal, setShowBuildingModal] = useState(false);
    const [editingBuilding, setEditingBuilding] = useState(null);
    const [buildingForm, setBuildingForm] = useState(EMPTY_BUILDING);
    const [deleteBuildingTarget, setDeleteBuildingTarget] = useState(null);

    // Floor modal
    const [showFloorModal, setShowFloorModal] = useState(false);
    const [floorParentBuilding, setFloorParentBuilding] = useState(null);
    const [editingFloor, setEditingFloor] = useState(null);
    const [floorForm, setFloorForm] = useState(EMPTY_FLOOR);
    const [deleteFloorTarget, setDeleteFloorTarget] = useState(null);

    // ── Building CRUD ──────────────────────────────────────────────────────────

    const openCreateBuilding = () => {
        setEditingBuilding(null);
        setBuildingForm(EMPTY_BUILDING);
        setShowBuildingModal(true);
    };

    const openEditBuilding = (b) => {
        setEditingBuilding(b.id);
        setBuildingForm({ name: b.name, address: b.address || '' });
        setShowBuildingModal(true);
    };

    const saveBuilding = () => {
        const payload = { ...buildingForm, project: projectId };
        const req = editingBuilding
            ? axiosInstance.put(`building/${editingBuilding}/`, payload)
            : axiosInstance.post('building/', payload);
        req.then(() => { setShowBuildingModal(false); onRefresh(); });
    };

    const deleteBuilding = () => {
        axiosInstance.delete(`building/${deleteBuildingTarget.id}/`)
            .then(() => { setDeleteBuildingTarget(null); onRefresh(); });
    };

    // ── Floor CRUD ─────────────────────────────────────────────────────────────

    const openCreateFloor = (building) => {
        setFloorParentBuilding(building);
        setEditingFloor(null);
        setFloorForm(EMPTY_FLOOR);
        setShowFloorModal(true);
    };

    const openEditFloor = (building, floor) => {
        setFloorParentBuilding(building);
        setEditingFloor(floor.id);
        setFloorForm({
            floor_number: floor.floor_number,
            name: floor.name,
            width_meters: floor.width_meters || '',
            height_meters: floor.height_meters || '',
            path_loss_exponent: floor.path_loss_exponent ?? 2.5,
            floorPlanFile: null,
            existingFloorPlanUrl: floor.floor_plan_url || null,
        });
        setShowFloorModal(true);
    };

    const saveFloor = () => {
        const fd = new FormData();
        fd.append('building', floorParentBuilding.id);
        fd.append('floor_number', floorForm.floor_number);
        fd.append('name', floorForm.name);
        fd.append('path_loss_exponent', floorForm.path_loss_exponent);
        if (floorForm.width_meters) fd.append('width_meters', floorForm.width_meters);
        if (floorForm.height_meters) fd.append('height_meters', floorForm.height_meters);
        if (floorForm.floorPlanFile) fd.append('floor_plan', floorForm.floorPlanFile);

        const req = editingFloor
            ? axiosInstance.put(`building_floor/${editingFloor}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            : axiosInstance.post('building_floor/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        req.then(() => { setShowFloorModal(false); onRefresh(); });
    };

    const deleteFloor = () => {
        axiosInstance.delete(`building_floor/${deleteFloorTarget.id}/`)
            .then(() => { setDeleteFloorTarget(null); onRefresh(); });
    };

    const setBF = (k, v) => setBuildingForm(f => ({ ...f, [k]: v }));
    const setFF = (k, v) => setFloorForm(f => ({ ...f, [k]: v }));

    return (
        <div>
            {/* Buildings header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1E293B' }}>{t('gps.indoor.buildings')}</div>
                <button onClick={openCreateBuilding} style={primaryBtn}>
                    <FontAwesomeIcon icon={faPlus} /> {t('gps.indoor.newBuilding')}
                </button>
            </div>

            {buildings.length === 0 ? (
                <div style={{ color: '#94A3B8', fontSize: '13px', padding: '16px 0' }}>{t('gps.indoor.noBuildings')}</div>
            ) : (
                buildings.map(b => (
                    <div key={b.id} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '10px', overflow: 'hidden' }}>
                        {/* Building row */}
                        <div
                            onClick={() => setExpandedBuilding(expandedBuilding === b.id ? null : b.id)}
                            style={{ background: '#F8FAFC', padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FontAwesomeIcon icon={faBuilding} style={{ color: '#4F46E5', fontSize: '12px' }} />
                                <span style={{ fontWeight: '600', fontSize: '13px', color: '#1E293B' }}>{b.name}</span>
                                {b.address && <span style={{ fontSize: '11px', color: '#94A3B8' }}>{b.address}</span>}
                                <span style={{ fontSize: '11px', color: '#64748B', background: '#E2E8F0', borderRadius: '4px', padding: '1px 7px' }}>{b.floor_count} {t('gps.indoor.floors').toLowerCase()}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={e => { e.stopPropagation(); openEditBuilding(b); }} style={iconBtn('#4F46E5')}><FontAwesomeIcon icon={faPencil} /></button>
                                <button onClick={e => { e.stopPropagation(); setDeleteBuildingTarget(b); }} style={iconBtn('#EF4444')}><FontAwesomeIcon icon={faTrash} /></button>
                            </div>
                        </div>

                        {/* Floors list */}
                        {expandedBuilding === b.id && (
                            <div style={{ padding: '10px 14px' }}>
                                {b.floors?.length === 0 && (
                                    <div style={{ color: '#94A3B8', fontSize: '12px', marginBottom: '8px' }}>{t('gps.indoor.noFloors')}</div>
                                )}
                                {b.floors?.map(f => (
                                    <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FontAwesomeIcon icon={faLayerGroup} style={{ color: '#0F766E', fontSize: '11px' }} />
                                            <span style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>Floor {f.floor_number}: {f.name}</span>
                                            {f.floor_plan_url && <span style={{ fontSize: '10px', color: '#22C55E', background: '#F0FDF4', borderRadius: '4px', padding: '1px 6px' }}>✓ Plan</span>}
                                            <span style={{ fontSize: '10px', color: '#64748B' }}>n={f.path_loss_exponent}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button onClick={() => openEditFloor(b, f)} style={iconBtn('#4F46E5')}><FontAwesomeIcon icon={faPencil} /></button>
                                            <button onClick={() => setDeleteFloorTarget(f)} style={iconBtn('#EF4444')}><FontAwesomeIcon icon={faTrash} /></button>
                                        </div>
                                    </div>
                                ))}
                                <button onClick={() => openCreateFloor(b)} style={{ ...primaryBtn, marginTop: '10px', fontSize: '12px', padding: '5px 12px' }}>
                                    <FontAwesomeIcon icon={faPlus} /> {t('gps.indoor.newFloor')}
                                </button>
                            </div>
                        )}
                    </div>
                ))
            )}

            {/* Building create/edit modal */}
            <Modal show={showBuildingModal} setter={setShowBuildingModal}
                name={editingBuilding ? t('gps.indoor.editBuilding') : t('gps.indoor.newBuilding')}
                buttonName={t('common.save', 'Save')} onClick={saveBuilding}>
                <label style={labelStyle}>{t('gps.indoor.buildingName')}</label>
                <input value={buildingForm.name} onChange={e => setBF('name', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.address')}</label>
                <input value={buildingForm.address} onChange={e => setBF('address', e.target.value)} style={inputStyle} />
            </Modal>

            {/* Floor create/edit modal */}
            <Modal show={showFloorModal} setter={setShowFloorModal}
                name={editingFloor ? t('gps.indoor.editFloor') : t('gps.indoor.newFloor')}
                buttonName={t('common.save', 'Save')} onClick={saveFloor}>
                <label style={labelStyle}>{t('gps.indoor.floorNumber')}</label>
                <input type="number" value={floorForm.floor_number} onChange={e => setFF('floor_number', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.floorName')}</label>
                <input value={floorForm.name} onChange={e => setFF('name', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.uploadFloorPlan')}</label>
                <label style={{
                    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                    border: '1px dashed #CBD5E1', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#64748B',
                }}>
                    <FontAwesomeIcon icon={faUpload} />
                    {floorForm.floorPlanFile
                        ? floorForm.floorPlanFile.name
                        : floorForm.existingFloorPlanUrl
                            ? floorForm.existingFloorPlanUrl.split('/').pop().split('?')[0]
                            : 'Choose image (PNG, JPG)'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => setFF('floorPlanFile', e.target.files[0] || null)} />
                </label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{t('gps.indoor.widthMeters')}</label>
                        <input type="number" value={floorForm.width_meters} readOnly style={{ ...inputStyle, background: '#F8FAFC', color: '#94A3B8', cursor: 'not-allowed' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{t('gps.indoor.heightMeters')}</label>
                        <input type="number" value={floorForm.height_meters} readOnly style={{ ...inputStyle, background: '#F8FAFC', color: '#94A3B8', cursor: 'not-allowed' }} />
                    </div>
                </div>
                <label style={labelStyle}>{t('gps.indoor.pathLossExponent')}</label>
                <input type="number" step="0.1" value={floorForm.path_loss_exponent} onChange={e => setFF('path_loss_exponent', e.target.value)} style={inputStyle} />
                <div style={{ fontSize: '11px', color: '#94A3B8' }}>{t('gps.indoor.pathLossHint')}</div>
            </Modal>

            {/* Delete building confirm */}
            <Modal show={!!deleteBuildingTarget} setter={() => setDeleteBuildingTarget(null)}
                name={t('gps.indoor.deleteBuilding')} buttonName={t('common.delete', 'Delete')} onClick={deleteBuilding} danger>
                <p style={{ fontSize: '14px', color: '#475569' }}>Delete building <strong>{deleteBuildingTarget?.name}</strong>?</p>
            </Modal>

            {/* Delete floor confirm */}
            <Modal show={!!deleteFloorTarget} setter={() => setDeleteFloorTarget(null)}
                name={t('gps.indoor.deleteFloor')} buttonName={t('common.delete', 'Delete')} onClick={deleteFloor} danger>
                <p style={{ fontSize: '14px', color: '#475569' }}>Delete floor <strong>{deleteFloorTarget?.name}</strong>?</p>
            </Modal>
        </div>
    );
}

const primaryBtn = {
    background: '#4F46E5', border: 'none', borderRadius: '6px', padding: '7px 14px',
    color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '6px',
};
const iconBtn = (color) => ({
    background: 'none', border: 'none', cursor: 'pointer', color, fontSize: '13px', padding: '3px 6px',
});
